import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, fromEvent, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { Executor } from 'src/util/executor';
import { SessionStorage } from 'src/util/storage/session-storage';

enum TabStatus {
  Pending = 'Pending',
  Active = 'Active',
  Inactive = 'Inactive',
}

class Message {
  constructor(
    public type: 'check-tab' | 'dup-tab' | 'not-active',
    public payload: { randId: number; tab: number; forceUpdate?: boolean },
  ) {}
}

interface TabSettings {
  tabId: string;
  tab: number;
}
class TabStorageManager {
  private static STORAGE_KEY = 'tcs-tab';
  private _incrementBy = -1;

  public get settings() {
    return this._settings;
  }

  constructor(private _settings: TabSettings, private _storage: SessionStorage) {}

  public static createSettings(tab: number, tabId?: string): TabSettings {
    return { tab, tabId: tabId ?? `${tab}` };
  }
  public static fromSettings(tabSettings: TabSettings, storage: SessionStorage) {
    return new TabStorageManager(tabSettings, storage);
  }
  public static getSavedTab() {
    const storage = new SessionStorage({
      name: 'TcsTab',
      setItemExecutor: new Executor({
        strategies: [
          // continue silently without throwing error
          {
            name: 'operation',
            canRetry: () => false,
            execute: (operation) => operation(),
          },
        ],
      }),
    });
    const tabSettings = JSON.parse(
      storage.getItem(this.STORAGE_KEY) ?? JSON.stringify(this.createSettings(0)),
    );
    return this.fromSettings(tabSettings, storage);
  }

  public generateTabNumber(tabNumber: number) {
    this._incrementBy = this._incrementBy < 0 && tabNumber + this._incrementBy > 0 ? -1 : 1;
    return tabNumber + this._incrementBy;
  }

  public save(settings?: Partial<TabSettings>) {
    this._settings = { ...this.settings, ...settings };
    this._storage.setItem(TabStorageManager.STORAGE_KEY, JSON.stringify(this.settings));
  }
}

function getBrowser(): string {
  const agent =
    'navigator' in window && 'userAgent' in window.navigator
      ? window.navigator.userAgent.toLowerCase()
      : '';
  return agent.indexOf('edge') > -1
    ? 'ie-edge' // old
    : agent.indexOf('edg') > -1
    ? 'edge' // new
    : agent.indexOf('opr') > -1 && 'opr' in window
    ? 'opera'
    : agent.indexOf('chrome') > -1 && 'chrome' in window
    ? 'chrome'
    : agent.indexOf('trident') > -1
    ? 'ie'
    : agent.indexOf('firefox') > -1
    ? 'firefox'
    : agent.indexOf('safari') > -1
    ? 'safari'
    : 'other';
}

@Injectable({
  providedIn: 'root',
})
export class TabsService implements OnDestroy {
  private _onDestroy$ = new Subject();
  private _channel = new BroadcastChannel('tabs-service');
  private _tabManager = TabStorageManager.getSavedTab();
  private _tabStatus$ = new BehaviorSubject<TabStatus>(TabStatus.Pending);

  private get _isTabActive() {
    return this._tabStatus$.value === TabStatus.Active;
  }

  public get tabId() {
    return this._tabManager.settings.tabId;
  }

  public get tab() {
    return this._tabManager.settings.tab;
  }

  public get isTabActive$() {
    return this._tabStatus$.pipe(map((status) => status === TabStatus.Active));
  }

  public get isTabPending$() {
    return this._tabStatus$.pipe(map((status) => status === TabStatus.Pending));
  }

  public get isTabActiveOrPending$() {
    return this._tabStatus$.pipe(
      map((status) => status === TabStatus.Active || status === TabStatus.Pending),
    );
  }

  constructor(private _router: Router) {}

  public init() {
    const randId = this._random();
    let initializing = true;

    const save = (tab: number) => {
      const tabId = `${getBrowser()}-${tab}|${Date.now()}`;
      this._tabManager.save(TabStorageManager.createSettings(tab, tabId));
    };

    const setActive = () => {
      // set current tab to active
      this._tabStatus$.next(TabStatus.Active);
      // tell all other tabs they're not active
      this._channel.postMessage(new Message('not-active', { randId, tab: this.tab }));
    };

    const setInactive = () => {
      this._tabStatus$.next(TabStatus.Inactive);
    };

    if (this._tabManager.settings.tab === 0) {
      // new tab
      save(1);
    }

    // check to make sure the current tab number doesn't already exist, if it does, randId will
    // determine the update priority (smallest randId should update its tab number)
    this._channel.postMessage(new Message('check-tab', { randId, tab: this.tab }));

    // listen for 'check-id' and 'duplicate' messages
    this._channel.addEventListener('message', (msg: MessageEvent<Message>) => {
      const recv = msg.data.payload;

      switch (msg.data.type) {
        case 'check-tab':
          if (this.tab === recv.tab) {
            if (initializing && randId < recv.randId) {
              // in initializing state, so set next tab and 'check-tab' for duplicates
              save(this._tabManager.generateTabNumber(recv.tab));
              this._channel.postMessage(new Message('check-tab', { randId, tab: this.tab }));
            } else if (!initializing && randId < recv.randId) {
              // not initializing, so current tab has priority, instead force duplicate to update its tab
              this._channel.postMessage(
                new Message('dup-tab', { randId, tab: this.tab, forceUpdate: true }),
              );
            } else {
              // current tab has priority, so respond with duplicate message
              this._channel.postMessage(new Message('dup-tab', { randId, tab: this.tab }));
            }
          }
          break;
        case 'dup-tab':
          // only check for duplicates on tabs that are being initialized
          const canUpdate = initializing && (randId < recv.randId || (recv.forceUpdate ?? false));
          if (canUpdate && this.tab === recv.tab) {
            // tab was a duplicate so increment and check again
            save(this._tabManager.generateTabNumber(recv.tab));
            this._channel.postMessage(new Message('check-tab', { randId, tab: this.tab }));
          }
          break;
        case 'not-active':
          setInactive();
          break;
      }
    });

    fromEvent(document, 'visibilitychange')
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(() => {
        if (document.hidden) {
          setInactive();
        } else {
          setActive();
        }
      });
    fromEvent(document, 'mouseenter')
      .pipe(
        filter(() => !this._isTabActive),
        takeUntil(this._onDestroy$),
      )
      .subscribe(() => setActive());

    this._router.events
      .pipe(
        filter((ev) => ev instanceof NavigationEnd),
        take(1),
      )
      .subscribe(() => {
        // reset after navigation completes
        initializing = false;
      });
  }

  public getStatus(): { tab: number; isTabActive: boolean } {
    return { tab: this.tab, isTabActive: this._isTabActive };
  }

  private _random() {
    // Math.random() can create duplicates when window loads multiple tabs at a time. For example restoring the browser
    // crypto lib doesn't suffer from this issue
    return crypto.getRandomValues(new Uint32Array(1))[0];
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }
}
